<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore } from '@/stores/uiStore'
import { useI18n } from '@/i18n'
import { toggleTheme } from '@/composables/useTheme'
import { formatKeys, keysFor, SHORTCUTS } from '@/composables/useShortcuts'
import * as actions from '@/app/actions'
import type { I18nKey } from '@/i18n'
import { ADJUSTMENT_KINDS } from '@/model/defaults'
import { listFilters, FILTER_CATEGORIES } from '@/filters/registry'
import { toast } from '@/composables/useToast'
import { bridgeEnabled, bridgePort, toggleBridge, setBridgePort } from '@/mcp/bridgeClient'
import { runCommand } from '@/commands/registry'
import { resetToolHints } from '@/composables/useToolHints'
import { requestAiAction } from '@/ai/actions'

const cmd = (id: string, params: Record<string, unknown> = {}) => () => void runCommand(id, params)

const { t, locale, setLocale } = useI18n()
const docStore = useDocumentStore()
const ui = useUiStore()

interface Item {
  label: I18nKey
  run: () => void
  shortcutId?: string
  disabled?: () => boolean
  separatorBefore?: boolean
}
interface Menu {
  id: string
  label: I18nKey
  items: Item[]
}

const hasDoc = () => !docStore.hasDocument
const menus = computed<Menu[]>(() => [
  {
    id: 'file',
    label: 'menu.file',
    items: [
      { label: 'menu.file.new', run: () => (ui.dialog = 'new'), shortcutId: 'new' },
      { label: 'menu.file.open', run: () => void actions.openFilePicker(), shortcutId: 'open' },
      { label: 'menu.file.save', run: () => void actions.saveProject(), shortcutId: 'save', disabled: hasDoc, separatorBefore: true },
      { label: 'menu.file.export', run: () => (ui.dialog = 'export'), shortcutId: 'export', disabled: hasDoc },
      { label: 'menu.file.exportPsd', run: () => void actions.exportPsd(), disabled: hasDoc },
      { label: 'menu.file.exportLayers', run: () => void actions.exportLayersZip(), disabled: hasDoc },
      { label: 'menu.file.close', run: actions.closeDocument, disabled: hasDoc, separatorBefore: true }
    ]
  },
  {
    id: 'edit',
    label: 'menu.edit',
    items: [
      { label: 'menu.edit.undo', run: actions.undo, shortcutId: 'undo', disabled: () => !docStore.canUndo },
      { label: 'menu.edit.redo', run: actions.redo, shortcutId: 'redo', disabled: () => !docStore.canRedo },
      { label: 'menu.edit.cut', run: () => void actions.cutSelection(), shortcutId: 'cut', disabled: () => !docStore.activeLayer, separatorBefore: true },
      { label: 'menu.edit.copy', run: () => void actions.copySelection(), shortcutId: 'copy', disabled: () => !docStore.activeLayer },
      { label: 'menu.edit.paste', run: () => void actions.pasteAsLayer(), shortcutId: 'paste' },
      { label: 'menu.edit.clear', run: cmd('edit.clear'), disabled: () => !docStore.activeLayer },
      { label: 'menu.edit.contentAwareFill', run: cmd('edit.contentAwareFill'), disabled: () => !docStore.hasSelection || docStore.activeLayer?.kind !== 'raster' },
      { label: 'menu.edit.fill', run: () => (ui.dialog = 'fill'), shortcutId: 'fill', disabled: () => docStore.activeLayer?.kind !== 'raster', separatorBefore: true },
      { label: 'menu.edit.stroke', run: () => (ui.dialog = 'stroke'), disabled: () => !docStore.hasSelection || docStore.activeLayer?.kind !== 'raster' },
      { label: 'menu.edit.definePattern', run: () => { const n = prompt(t('menu.edit.definePattern'), ''); if (n !== null) void runCommand('pattern.define', n ? { name: n } : {}).then(() => toast.success(t('pattern.defined'))) }, disabled: hasDoc },
      { label: 'menu.edit.defineBrush', run: () => { const n = prompt(t('menu.edit.defineBrush'), ''); if (n !== null) void runCommand('brush.define', n ? { name: n } : {}).then(() => toast.success(t('brush.defined'))) }, disabled: hasDoc },
      { label: 'menu.edit.freeTransform', run: actions.startFreeTransform, shortcutId: 'freeTransform', disabled: () => docStore.activeLayer?.kind !== 'raster' && docStore.activeLayer?.kind !== 'shape' && docStore.activeLayer?.kind !== 'smart', separatorBefore: true }
    ]
  },
  {
    id: 'image',
    label: 'menu.image',
    items: [
      { label: 'menu.image.size', run: () => (ui.dialog = 'imageSize'), shortcutId: 'imageSize', disabled: hasDoc },
      { label: 'menu.image.canvasSize', run: () => (ui.dialog = 'canvasSize'), shortcutId: 'canvasSize', disabled: hasDoc },
      { label: 'menu.image.rotate90', run: cmd('image.rotate', { degrees: 90 }), disabled: hasDoc, separatorBefore: true },
      { label: 'menu.image.rotate270', run: cmd('image.rotate', { degrees: 270 }), disabled: hasDoc },
      { label: 'menu.image.rotate180', run: cmd('image.rotate', { degrees: 180 }), disabled: hasDoc },
      { label: 'menu.image.flipH', run: cmd('image.flip', { axis: 'h' }), disabled: hasDoc },
      { label: 'menu.image.flipV', run: cmd('image.flip', { axis: 'v' }), disabled: hasDoc },
      { label: 'menu.image.rotateArbitrary', run: () => (ui.dialog = 'rotateArbitrary'), disabled: hasDoc },
      { label: 'menu.image.trim', run: cmd('image.trim'), disabled: hasDoc, separatorBefore: true },
      { label: 'menu.image.autoTone', run: cmd('image.autoTone'), shortcutId: 'autoTone', disabled: hasDoc, separatorBefore: true },
      { label: 'menu.image.autoContrast', run: cmd('image.autoContrast'), shortcutId: 'autoContrast', disabled: hasDoc },
      { label: 'menu.image.flatten', run: cmd('image.flatten'), disabled: hasDoc, separatorBefore: true }
    ]
  },
  {
    id: 'select',
    label: 'menu.select',
    items: [
      { label: 'menu.select.all', run: cmd('selection.all'), shortcutId: 'selectAll', disabled: hasDoc },
      { label: 'menu.select.deselect', run: cmd('selection.none'), shortcutId: 'deselect', disabled: () => !docStore.hasSelection },
      { label: 'menu.select.invert', run: cmd('selection.invert'), shortcutId: 'invert', disabled: hasDoc },
      { label: 'menu.select.quickMask', run: () => (ui.quickMask = !ui.quickMask), shortcutId: 'quickMask', disabled: hasDoc, separatorBefore: true },
      { label: 'menu.select.saveChannel', run: () => { const n = prompt('名前', `アルファチャンネル ${(docStore.doc?.channels.length ?? 0) + 1}`); if (n !== null) docStore.saveSelectionAsChannel(n || undefined) }, disabled: () => !docStore.hasSelection },
      { label: 'menu.select.fromLayer', run: cmd('selection.fromLayer'), disabled: () => !docStore.activeLayer || docStore.activeLayer.kind === 'group' },
      { label: 'menu.select.feather', run: () => actions.openSelectionModify('feather'), disabled: () => !docStore.hasSelection, separatorBefore: true },
      { label: 'menu.select.expand', run: () => actions.openSelectionModify('expand'), disabled: () => !docStore.hasSelection },
      { label: 'menu.select.contract', run: () => actions.openSelectionModify('contract'), disabled: () => !docStore.hasSelection },
      { label: 'menu.select.border', run: () => actions.openSelectionModify('border'), disabled: () => !docStore.hasSelection },
      { label: 'menu.select.refine', run: () => (ui.dialog = 'refineEdge'), disabled: () => !docStore.hasSelection },
      { label: 'menu.select.colorRange', run: () => (ui.dialog = 'colorRange'), disabled: hasDoc, separatorBefore: true },
      { label: 'menu.select.subject', run: () => requestAiAction('selectSubject'), disabled: hasDoc }
    ]
  },
  {
    id: 'layer',
    label: 'menu.layer',
    items: [
      { label: 'menu.layer.new', run: cmd('layer.add'), shortcutId: 'newLayer', disabled: hasDoc },
      { label: 'menu.layer.duplicate', run: cmd('layer.duplicate'), disabled: () => !docStore.activeLayer },
      { label: 'menu.layer.newFillSolid', run: () => docStore.addFillLayer({ kind: 'solid', hex: ui.foreground }), disabled: hasDoc, separatorBefore: true },
      { label: 'menu.layer.newFillPattern', run: () => { const p = docStore.doc?.patterns[0]; if (!p) { toast.warn(t('pattern.none')); return } docStore.addFillLayer({ kind: 'pattern', patternId: p.id, scale: 1, offset: { x: 0, y: 0 } }) }, disabled: () => !docStore.doc?.patterns.length },
      { label: 'menu.layer.newFillGradient', run: () => docStore.addFillLayer({ kind: 'gradient', style: 'linear', stops: [{ pos: 0, hex: ui.foreground, alpha: 1 }, { pos: 1, hex: ui.background, alpha: 1 }], angle: 90, scale: 1, reverse: false }), disabled: hasDoc },
      { label: 'menu.layer.delete', run: actions.deleteActiveLayer, shortcutId: 'deleteLayer', disabled: () => !docStore.activeLayer },
      { label: 'menu.layer.viaCopy', run: cmd('layer.newViaCopy'), shortcutId: 'viaCopy', disabled: () => docStore.activeLayer?.kind !== 'raster', separatorBefore: true },
      { label: 'menu.layer.viaCut', run: cmd('layer.newViaCopy', { cut: true }), shortcutId: 'viaCut', disabled: () => docStore.activeLayer?.kind !== 'raster' },
      { label: 'menu.layer.mergeDown', run: cmd('layer.mergeDown'), shortcutId: 'mergeDown', disabled: () => !docStore.activeLayer, separatorBefore: true },
      { label: 'menu.layer.mergeVisible', run: cmd('layer.mergeVisible'), shortcutId: 'mergeVisible', disabled: hasDoc },
      { label: 'menu.layer.group', run: actions.groupActiveLayers, shortcutId: 'group', disabled: () => !docStore.activeLayer, separatorBefore: true },
      { label: 'menu.layer.ungroup', run: () => { const l = docStore.activeLayer; if (l?.kind === 'group') docStore.ungroupLayer(l.id) }, disabled: () => docStore.activeLayer?.kind !== 'group' },
      { label: 'menu.layer.clip', run: actions.toggleClipActiveLayer, shortcutId: 'clip', disabled: () => !docStore.activeLayer },
      { label: 'menu.layer.removeBackground', run: () => requestAiAction('removeBackground'), disabled: () => docStore.activeLayer?.kind !== 'raster', separatorBefore: true },
      { label: 'menu.layer.addMaskReveal', run: () => { const l = docStore.activeLayer; if (l) docStore.addMask(l.id, 'reveal') }, disabled: () => !docStore.activeLayer || !!docStore.activeLayer.mask, separatorBefore: true },
      { label: 'menu.layer.addMaskHide', run: () => { const l = docStore.activeLayer; if (l) docStore.addMask(l.id, 'hide') }, disabled: () => !docStore.activeLayer || !!docStore.activeLayer.mask },
      { label: 'menu.layer.toggleMask', run: () => { const l = docStore.activeLayer; if (l?.mask) docStore.setMaskEnabled(l.id, !l.mask.enabled) }, disabled: () => !docStore.activeLayer?.mask },
      { label: 'menu.layer.removeMask', run: () => { const l = docStore.activeLayer; if (l) docStore.removeMask(l.id) }, disabled: () => !docStore.activeLayer?.mask },
      { label: 'menu.layer.style', run: () => (ui.dialog = 'layerStyle'), disabled: () => !docStore.activeLayer || docStore.activeLayer.kind === 'group', separatorBefore: true },
      { label: 'menu.layer.convertToSmart', run: cmd('layer.convertToSmart'), disabled: () => docStore.activeLayer?.kind !== 'raster', separatorBefore: true },
      { label: 'menu.layer.rasterizeSmart', run: cmd('layer.rasterizeSmart'), disabled: () => docStore.activeLayer?.kind !== 'smart' },
      { label: 'menu.layer.strokePath', run: cmd('path.stroke'), disabled: () => docStore.activeLayer?.kind !== 'shape' },
      { label: 'menu.layer.fillPath', run: cmd('path.fill'), disabled: () => docStore.activeLayer?.kind !== 'shape' },
      { label: 'menu.layer.rasterizeShape', run: () => { const l = docStore.activeLayer; if (l?.kind === 'shape') docStore.rasterizeShapeLayer(l.id) }, disabled: () => docStore.activeLayer?.kind !== 'shape' },
      { label: 'menu.layer.rasterizeText', run: () => { const l = docStore.activeLayer; if (l?.kind === 'text') docStore.rasterizeTextLayer(l.id) }, disabled: () => docStore.activeLayer?.kind !== 'text', separatorBefore: true },
      { label: 'menu.layer.flipH', run: cmd('layer.flipRotate', { op: 'flipH' }), disabled: () => docStore.activeLayer?.kind !== 'raster', separatorBefore: true },
      { label: 'menu.layer.flipV', run: cmd('layer.flipRotate', { op: 'flipV' }), disabled: () => docStore.activeLayer?.kind !== 'raster' },
      { label: 'menu.layer.rotate90', run: cmd('layer.flipRotate', { op: 'rot90' }), disabled: () => docStore.activeLayer?.kind !== 'raster' },
      { label: 'menu.layer.rotate270', run: cmd('layer.flipRotate', { op: 'rot270' }), disabled: () => docStore.activeLayer?.kind !== 'raster' },
      { label: 'menu.layer.rotate180', run: cmd('layer.flipRotate', { op: 'rot180' }), disabled: () => docStore.activeLayer?.kind !== 'raster' },
      { label: 'align.left', run: cmd('layer.align', { mode: 'left' }), disabled: () => !docStore.activeLayer, separatorBefore: true },
      { label: 'align.hcenter', run: cmd('layer.align', { mode: 'hcenter' }), disabled: () => !docStore.activeLayer },
      { label: 'align.right', run: cmd('layer.align', { mode: 'right' }), disabled: () => !docStore.activeLayer },
      { label: 'align.top', run: cmd('layer.align', { mode: 'top' }), disabled: () => !docStore.activeLayer },
      { label: 'align.vcenter', run: cmd('layer.align', { mode: 'vcenter' }), disabled: () => !docStore.activeLayer },
      { label: 'align.bottom', run: cmd('layer.align', { mode: 'bottom' }), disabled: () => !docStore.activeLayer },
      { label: 'align.distributeH', run: cmd('layer.distribute', { axis: 'h' }), disabled: () => (docStore.doc?.activeLayerIds.length ?? 0) < 3, separatorBefore: true },
      { label: 'align.distributeV', run: cmd('layer.distribute', { axis: 'v' }), disabled: () => (docStore.doc?.activeLayerIds.length ?? 0) < 3 }
    ]
  },
  {
    id: 'adjust',
    label: 'menu.layer.newAdjustment',
    items: ADJUSTMENT_KINDS.map(k => ({ label: `adj.${k}` as I18nKey, run: cmd('layer.addAdjustment', { kind: k }), disabled: hasDoc }))
  },
  {
    id: 'filter',
    label: 'menu.filter',
    items: FILTER_CATEGORIES.flatMap((cat, ci) =>
      listFilters()
        .filter(f => f.category === cat.id)
        .map((f, i) => ({
          label: f.label,
          run: () => {
            if (docStore.activeLayer?.kind !== 'raster') {
              toast.warn(t('filter.needRaster'))
              return
            }
            ui.dialogArg = f.id
            ui.dialog = 'filter'
          },
          disabled: () => docStore.activeLayer?.kind !== 'raster',
          separatorBefore: i === 0 && ci > 0
        }))
    ).concat([
      {
        label: 'filter.liquify' as I18nKey,
        run: () => {
          if (docStore.activeLayer?.kind !== 'raster') {
            toast.warn(t('filter.needRaster'))
            return
          }
          ui.dialog = 'liquify'
        },
        disabled: () => docStore.activeLayer?.kind !== 'raster',
        separatorBefore: true
      }
    ])
  },
  {
    id: 'view',
    label: 'menu.view',
    items: [
      { label: 'menu.view.zoomIn', run: () => ui.zoomStep(1), shortcutId: 'zoomIn', disabled: hasDoc },
      { label: 'menu.view.zoomOut', run: () => ui.zoomStep(-1), shortcutId: 'zoomOut', disabled: hasDoc },
      { label: 'menu.view.actual', run: actions.zoomActual, shortcutId: 'actual', disabled: hasDoc },
      { label: 'menu.view.fit', run: actions.zoomFit, shortcutId: 'fit', disabled: hasDoc },
      { label: 'menu.view.rulers', run: () => (ui.showRulers = !ui.showRulers), shortcutId: 'rulers', separatorBefore: true },
      { label: 'menu.view.guides', run: () => (ui.showGuides = !ui.showGuides), shortcutId: 'guides' },
      { label: 'menu.view.grid', run: () => (ui.showGrid = !ui.showGrid), shortcutId: 'grid' },
      { label: 'menu.view.snap', run: () => (ui.snapEnabled = !ui.snapEnabled) },
      { label: 'menu.view.gridSize', run: () => { const v = Number(prompt('Grid size (px)', String(ui.gridSize))); if (Number.isFinite(v) && v >= 2) ui.gridSize = Math.round(v) } },
      { label: 'menu.view.clearGuides', run: () => docStore.clearGuides(), disabled: () => !docStore.doc?.guides.length },
      { label: 'menu.view.theme', run: toggleTheme, separatorBefore: true },
      { label: 'menu.view.language', run: () => setLocale(locale.value === 'ja' ? 'en' : 'ja') }
    ]
  },
  {
    id: 'help',
    label: 'menu.help',
    items: [
      { label: 'menu.help.toolGuide', run: () => (ui.toolGuideOpen = !ui.toolGuideOpen), shortcutId: 'toolGuide' },
      { label: 'menu.help.shortcuts', run: () => (ui.dialog = 'shortcuts') },
      { label: 'menu.help.resetHints', run: () => { resetToolHints(); toast.success(t('guide.hintsReset')) } },
      { label: 'menu.help.bridge', run: () => { toggleBridge(); if (bridgeEnabled.value) toast.info(t('bridge.hint', { port: bridgePort.value }), 6000) }, separatorBefore: true },
      { label: 'menu.help.bridgePort', run: () => { const v = Number(prompt('MCP bridge port', String(bridgePort.value))); if (Number.isFinite(v) && v > 0 && v < 65536) setBridgePort(v) } },
      { label: 'menu.help.ai', run: () => { ui.dialogArg = null; ui.dialog = 'aiConsent' } },
      { label: 'menu.help.resetWorkspace', run: () => { if (confirm(t('workspace.resetConfirm'))) ui.resetWorkspace() } },
      { label: 'menu.help.about', run: () => (ui.dialog = 'about') }
    ]
  }
])

const open = ref<string | null>(null)
function shortcutText(id?: string): string {
  if (!id) return ''
  const s = SHORTCUTS.find(x => x.id === id)
  const k = s ? keysFor(s)[0] : undefined
  return k ? formatKeys(k) : ''
}
function toggle(id: string) {
  open.value = open.value === id ? null : id
}
function hover(id: string) {
  if (open.value) open.value = id
}
function runItem(item: Item) {
  open.value = null
  if (item.disabled?.()) return
  item.run()
}
function onDocClick(e: MouseEvent) {
  if (!(e.target instanceof HTMLElement) || !e.target.closest('.menubar')) open.value = null
}
onMounted(() => document.addEventListener('pointerdown', onDocClick))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocClick))
</script>

<template>
  <div class="menubar">
    <span class="brand">{{ t('app.name') }}</span>
    <div v-for="m in menus" :key="m.id" class="menu" :class="{ open: open === m.id }">
      <button class="ghost head" @click="toggle(m.id)" @pointerenter="hover(m.id)">{{ t(m.label) }}</button>
      <div v-if="open === m.id" class="dropdown">
        <template v-for="(item, i) in m.items" :key="i">
          <div v-if="item.separatorBefore" class="sep" />
          <button class="item" :disabled="item.disabled?.()" @click="runItem(item)">
            <span>{{ t(item.label) }}</span>
            <span class="keys mono muted">{{ shortcutText(item.shortcutId) }}</span>
          </button>
        </template>
      </div>
    </div>
    <span class="spacer" />
    <span v-if="docStore.doc" class="docname muted">
      {{ docStore.doc.meta.name }}<span v-if="docStore.isDirty" class="dirty" :title="t('unsaved.badge')">●</span>
    </span>
  </div>
</template>

<style scoped>
.menubar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 8px;
  background: var(--bg-1);
  font-size: 12px;
}
.brand {
  font-weight: 600;
  margin-right: 10px;
  color: var(--fg-1);
}
.menu {
  position: relative;
}
.head {
  padding: 3px 8px;
  border-radius: var(--radius-sm);
}
.menu.open .head {
  background: var(--bg-hover);
}
.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 240px;
  background: var(--bg-2);
  border: 1px solid var(--line-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: 4px;
  z-index: 50;
  display: flex;
  flex-direction: column;
}
.item {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  padding: 5px 10px;
}
.item:hover:not(:disabled) {
  background: var(--accent);
  color: #fff;
}
.item:hover:not(:disabled) .keys {
  color: #fff;
}
.sep {
  height: 1px;
  background: var(--line);
  margin: 4px 6px;
}
.docname {
  font-size: 11px;
}
.dirty {
  color: var(--accent);
  margin-left: 4px;
}
</style>
